import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  TableComponent,
  useColumnsCustomRenderConfig,
  type Column,
  type CustomColumnRenderConfig,
} from '@innovamat/glimmer-components';
import { useDevice } from '@innovamat/hooks';

import type { Student as ConmatStudent } from '../../../../../../modules/report/conmat-test-detail';
import type { Student as CosmosStudent } from '../../../../../../modules/report/cosmos-test-detail';
import type { Status } from '../../../../../../modules/test/test';
import { TestStatusCell } from '../../../test-and-results-table/components';
import { StudentNameCell } from './student-name-cell';

type StudentRow = {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  inClassroom: boolean;
};

type Props = {
  students: ConmatStudent[] | CosmosStudent[];
};

const customColumnsRenderConfig = (
  t: TFunction
): CustomColumnRenderConfig<StudentRow> => ({
  firstName: (value: string, row: StudentRow) => {
    return (
      <StudentNameCell
        value={value || t('common.unknown-student')}
        isDisabled={!row.inClassroom}
      />
    );
  },
  lastName: (value: string, row: StudentRow) => {
    return (
      <StudentNameCell
        value={value}
        isDisabled={!row.inClassroom}
        hasTooltip={!!value && !row.inClassroom}
        tooltipText={t(
          'mock-exams-drawer.table.student-changed-classroom.label'
        )}
      />
    );
  },
  status: (status: Status) => {
    return <TestStatusCell status={status} />;
  },
});

function StudentTable({ students }: Props): JSX.Element {
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  const columns: Column<StudentRow>[] = [
    {
      id: 'firstName',
      label: t('mock-exams-drawer.table.first-name.label'),
      sortable: true,
      sortIgnoreCase: true,
      width: 112,
      minWidth: 112,
      sticky: !isMobile,
    },
    {
      id: 'lastName',
      label: t('mock-exams-drawer.table.last-name.label'),
      sortable: true,
      sortIgnoreCase: true,
      width: 112,
      minWidth: 112,
      sticky: !isMobile,
    },
    {
      id: 'status',
      label: t('common.status'),
      width: 145,
      minWidth: 145,
      sticky: !isMobile,
    },
  ];

  const tableColumns = useColumnsCustomRenderConfig<StudentRow>(
    customColumnsRenderConfig(t),
    columns
  );

  const rows =
    students?.map((student: ConmatStudent | CosmosStudent) => ({
      id: student.id,
      firstName: student.firstName,
      lastName: student.lastName,
      status: student.status,
      inClassroom: student.inClassroom,
    })) ?? [];

  return (
    <TableComponent
      id="student-drawer-table"
      columns={tableColumns}
      rows={rows}
    />
  );
}

export { customColumnsRenderConfig, StudentTable };
